<script lang="ts" setup>
import {
  ref,
  provide,
  onMounted,
  onUnmounted,
  watch,
  computed,
  nextTick,
  useTemplateRef,
  onActivated,
  onDeactivated,
  type PropType,
} from 'vue';
import EmblaCarousel, { type EmblaCarouselType, type EmblaOptionsType, type EmblaPluginType } from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import type { AutoplayOptionsType } from 'embla-carousel-autoplay';
import { CONTEXT_KEY } from './types';

defineOptions({
  name: 'CarouselRoot',
});

const props = defineProps({
  loop: {
    type: Boolean,
    default: false,
  },

  autoplay: {
    type: [Boolean, Object] as PropType<boolean | Partial<AutoplayOptionsType>>,
    default: false,
  },

  slidesPerPage: {
    type: Number,
    default: 1,
  },

  spacing: {
    type: String,
    default: '0px',
  },

  align: {
    type: String,
  },
});

const emit = defineEmits<{
  select: [index: number]; // 幻灯片切换完成事件
  init: [api: EmblaCarouselType]; // 初始化完成事件
  destroy: []; // 销毁事件
}>();

const page = defineModel('page', { type: Number, default: 0 });

const emblaApi = ref<EmblaCarouselType | null>(null); // Embla 实例
const viewportRef = useTemplateRef<HTMLElement | null>('viewportRef'); // 视口元素的引用
const currentPage = ref(0); // 当前选中的页数 (内部状态)
const canScrollPrev = ref(false); // 是否可以向前滚动
const canScrollNext = ref(false); // 是否可以向后滚动
const pages = ref<number[]>([]);
const isPlaying = ref(false);

const slidesPerPage = computed(() => {
  return Math.max(1, props.slidesPerPage);
});

// 根据 props 计算需要加载的插件
const plugins = computed<EmblaPluginType[]>(() => {
  const activePlugins: EmblaPluginType[] = [];

  if (props.autoplay) {
    activePlugins.push(Autoplay(typeof props.autoplay === 'boolean' ? {
      stopOnInteraction: false,
      stopOnFocusIn: false,
      playOnInit: false,
    } : props.autoplay));
  }

  // 未来可以在这里添加其他插件

  return activePlugins;
});

const updateCarouselState = (api: EmblaCarouselType) => {
  if (!api) {
    return;
  }

  const newCurrentPage = api.selectedScrollSnap();

  currentPage.value = newCurrentPage;

  canScrollPrev.value = api.canScrollPrev();
  canScrollNext.value = api.canScrollNext();

  emit('select', newCurrentPage);

  const newPages = api.scrollSnapList().map((_, index) => index);

  if (pages.value.length !== newPages.length) {
    pages.value = newPages;
  }

  if (page.value !== undefined && page.value !== newCurrentPage) {
    page.value = newCurrentPage;
  }
};

const scrollTo = (index: number, jump: boolean = false) => {
  emblaApi.value?.scrollTo(index, jump);
};

const scrollPrev = () => {
  emblaApi.value?.scrollPrev();
};

const scrollNext = () => {
  emblaApi.value?.scrollNext();
};

const getOptions = () => {
  const options: EmblaOptionsType = {
    container: '.embla__container',
  };

  if (page.value !== undefined) {
    options.startIndex = page.value < 0 ? 0 : page.value;
  }

  if (props.loop) {
    options.loop = true;
  }

  return options;
};

const updatePlay = () => {
  isPlaying.value = true;
}

const updateStop = () => {
  isPlaying.value = false;
}

onMounted(async () => {
  if (!viewportRef.value) {
    return;
  }

  // 确保子组件渲染完成 (特别是 ItemGroup/Item)
  await nextTick();

  const viewportNode = viewportRef.value;

  if (!viewportNode) {
    console.error('Embla Carousel: 视口元素未找到');
    return;
  }

  // 初始化 Embla Carousel
  emblaApi.value = EmblaCarousel(viewportNode, getOptions(), plugins.value);

  emit('init', emblaApi.value);

  // 监听 Embla 事件以更新状态
  emblaApi.value
    .on('select', () => updateCarouselState(emblaApi.value!))
    .on('reInit', () => updateCarouselState(emblaApi.value!))
    .on('slidesChanged', () => updateCarouselState(emblaApi.value!))
    .on('resize', () => updateCarouselState(emblaApi.value!))
    .on('autoplay:play', updatePlay)
    .on('autoplay:stop', updateStop);

  updateCarouselState(emblaApi.value);

  if (pages.value.length > 1) {
    setTimeout(() => {
      const autoplay = emblaApi.value?.plugins().autoplay;

      autoplay?.play();
    }, 20);
  }

  watch(page, (newPage) => {
    if (newPage !== undefined && newPage !== currentPage.value) {
      scrollTo(newPage);
    }
  });

  watch([() => props.loop, plugins], () => {
    emblaApi.value?.reInit(getOptions(), plugins.value);
  });
});

onUnmounted(() => {
  if (emblaApi.value) {
    emblaApi.value.destroy();
    emblaApi.value = null;
    emit('destroy');
  }
});

onActivated(() => {
  if (emblaApi.value) {
    emblaApi.value.reInit(getOptions(), plugins.value);
  }

  const autoplay = emblaApi.value?.plugins().autoplay;

  if (pages.value.length > 1 && autoplay) {
    autoplay.play();
  }
})

onDeactivated(() => {
  const autoplay = emblaApi.value?.plugins().autoplay;

  if (autoplay) {
    autoplay.stop();
  }
})

provide(CONTEXT_KEY, {
  emblaApi,
  currentPage,
  slidesPerPage,
  pages,
  canScrollPrev,
  canScrollNext,
  isPlaying,
  scrollTo,
  scrollPrev,
  scrollNext,
});
</script>

<template>
  <div
    class="embla"
    data-scope="carousel"
    data-part="root"
    role="region"
    aria-roledescription="carousel"
    ref="viewportRef"
  >
    <slot></slot>
  </div>
</template>

<style lang="scss" scoped>
.embla {
  --slide-item-size: calc(100% / v-bind(props.slidesPerPage));
  --slide-spacing: v-bind(props.spacing);
}
</style>
